import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../AllProducts/AllProducts.css";
import "./MetalDetail.css";
import Navbar from "../../components/shared/Navbar/Navbar";
import Footer from "../../components/shared/Footer/Footer";
import BoardsDetailDisclaimer from "../../components/pages/AllProducts/Boards/BoardsDetailDisclaimer/BoardsDetailDisclaimer";
import BoardsDetailExploreSlider from "../../components/pages/AllProducts/Boards/BoardsDetailExploreSlider/BoardsDetailExploreSlider";
import AllSolutionsRequestNewProject from "../../components/pages/AllSolutions/AllSolutionsRequestNewProject/AllSolutionsRequestNewProject";
import { getMetalDetailById, metalDetailById } from "../../data/metalDetailData";
import { getAccessories, getAccessoryById } from "../../api/metals";
import { resolveMediaUrl } from "../../utils/media";
import {
  isSpecificationNoteRow,
  resolveSpecificationRowParts,
} from "../../utils/specificationTable";
import {
  buildDimensionColumnKeys,
  columnsFromAdmin,
  getDimensionValue,
  normalizeKey,
  sectionDefaultsFromLabelValuePairs,
  toFlexibleDimensionsTableRows,
  toHeaderLabel,
} from "../../utils/metalFrameDimensionsTable";
import { formatUnitSuperscriptText } from "../../utils/unitSuperscriptText";
import {
  expandMaterialPropertyRows,
  stressDefaultsForSectionDimensions,
} from "../../utils/yieldUltimateStressMaterial";
import { filterPdfRowsByProductSlug, mapPublicProductPdfs, pickPublicPdfArray } from "../../utils/productPdfs";
import { filterPublishedProducts } from "../../utils/publicProducts";
import SolutionDetailsDownloads from "../../components/pages/SolutionDetails/SolutionDetailsDownloads/SolutionDetailsDownloads";

const TAB_ITEMS = [
  { id: "standards", labelKey: "sections.products.detailTabs.standards" },
  { id: "specifications", labelKey: "sections.products.detailTabs.specifications" },
  { id: "dimensions", labelKey: "sections.products.detailTabs.dimensions" },
];

// Fiberglass insulation is sold in two product forms; the Dimensions table renders one row
// per form. Each row pulls data from the matching `dimensionsTable` entry in the dashboard.
const FIBERGLASS_TYPE_LABELS = ["Rolls", "Blankets"];

function renderMetalSpecificationTableBody(rows) {
  return rows.map((row, idx) => {
    if (isSpecificationNoteRow(row)) {
      const parts = resolveSpecificationRowParts(row);
      return (
        <tr key={`${String(row.label)}-${idx}`} className="metalSpecsTrNote">
          <td colSpan={4} className="metalSpecsTdNote">
            {parts.specificationText ? (
              <p className="metalSpecsNoteTitle">{formatUnitSuperscriptText(parts.specificationText)}</p>
            ) : null}
            {parts.valueText ? (
              <p className="metalSpecsNoteBody">{formatUnitSuperscriptText(parts.valueText)}</p>
            ) : null}
          </td>
        </tr>
      );
    }
    const parts = resolveSpecificationRowParts(row);
    const std = row.reference || "—";
    return (
      <tr key={`${String(row.label)}-${idx}`}>
        <td className="metalSpecsTdSpec">{parts.specificationText}</td>
        <td className="metalSpecsTdUnit">{parts.unit || "—"}</td>
        <td className="metalSpecsTdVal">{parts.valueText}</td>
        <td className="metalSpecsTdStd">{std}</td>
      </tr>
    );
  });
}

// Normalized keys for the Yield stress / Ultimate stress columns — these are hidden from every
// dimensions table on this page (matches the same filter used in the metal-frame detail pages).
const HIDDEN_STRESS_KEYS = new Set(["yieldstressfy", "ultimatestressfu"]);

const isStressLabel = (value) => {
  const text = String(value || "").toLowerCase();
  return /yield\s*stress/.test(text) || /ultimate\s*stress/.test(text);
};

function buildCrossSectionTableState(sectionDimsList, dimTableList, materialPropsRows, adminColumns) {
  const baseDefaults = sectionDefaultsFromLabelValuePairs(sectionDimsList);
  const stressDefaults = stressDefaultsForSectionDimensions(materialPropsRows || []);
  const sectionDimensionDefaults = { ...baseDefaults, ...stressDefaults };
  let dimensionsTable = toFlexibleDimensionsTableRows(dimTableList);
  if (dimensionsTable.length === 0 && Object.keys(sectionDimensionDefaults).length > 0) {
    dimensionsTable = [{}];
  }
  // Saved admin columns are authoritative: render exactly what the API returns.
  const adminCols = columnsFromAdmin(adminColumns);
  const dimensionColumns = adminCols
    ? adminCols.map((c) => c.key)
    : buildDimensionColumnKeys(dimensionsTable, sectionDimensionDefaults).filter(
        (key) => !HIDDEN_STRESS_KEYS.has(String(key || "").toLowerCase().replace(/[^a-z0-9]/g, ""))
      );
  return { sectionDimensionDefaults, dimensionsTable, dimensionColumns };
}

const MetalDetail = () => {
  const { metalId } = useParams();
  const { t, i18n } = useTranslation();
  const fallbackMetal = getMetalDetailById(metalId);
  const [apiMetal, setApiMetal] = useState(null);
  /** List row from GET /accessories — may include `pdfs` / `pdfDocuments` when detail omits them. */
  const [listMatch, setListMatch] = useState(null);
  const [apiSettled, setApiSettled] = useState(false);
  const [resolved, setResolved] = useState(Boolean(fallbackMetal));
  const [activeTab, setActiveTab] = useState("standards");
  const sectionRefs = useRef({
    standards: null,
    specifications: null,
    dimensions: null,
  });

  useEffect(() => {
    let isMounted = true;
    setApiSettled(false);
    setResolved(Boolean(fallbackMetal));

    const load = async () => {
      const language = String(i18n.language || "en").toLowerCase();
      try {
        const data = await getAccessories({ language });
        const list = filterPublishedProducts(data);
        const idOrKey = String(metalId ?? "").trim();
        const idOrKeyLc = idOrKey.toLowerCase();
        let matched = list.find(
          (item) =>
            String(item?.id ?? "") === idOrKey ||
            String(item?.accessoryKey ?? "").toLowerCase() === idOrKeyLc
        );
        if (isMounted) {
          setListMatch(matched || null);
        }

        // Full product DTO (includes `pdfs`) comes from GET /accessories/{id}, not always from the list.
        if (matched?.id != null) {
          if (matched.isActive === false || matched.isPublished === false) {
            if (isMounted) {
              setApiMetal(null);
              setListMatch(null);
              setResolved(true);
              setApiSettled(true);
            }
            return;
          }
          try {
            const detail = await getAccessoryById({ id: matched.id, language });
            if (isMounted) {
              if (detail && detail.isActive !== false && detail.isPublished !== false) {
                setApiMetal(detail || matched);
                setResolved(true);
              } else {
                setApiMetal(null);
                setListMatch(null);
                setResolved(true);
              }
              setApiSettled(true);
            }
            return;
          } catch {
            // use list row below
          }
        }

        const numericId = Number(metalId);
        if (!matched && Number.isFinite(numericId)) {
          try {
            const detail = await getAccessoryById({ id: numericId, language });
            if (isMounted) {
              if (detail && detail.isActive !== false && detail.isPublished !== false) {
                setApiMetal(detail || null);
                setResolved(true);
              } else {
                setApiMetal(null);
                setListMatch(null);
                setResolved(true);
              }
              setApiSettled(true);
            }
            return;
          } catch {
            // fall through
          }
        }

        if (isMounted) {
          setApiMetal(matched || null);
          setResolved(true);
          setApiSettled(true);
        }
      } catch (_error) {
        if (isMounted) {
          setApiMetal(null);
          setListMatch(null);
          setResolved(true);
          setApiSettled(true);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [fallbackMetal, i18n.language, metalId]);

  const metal = useMemo(() => {
    if (apiSettled && !apiMetal && !listMatch) return null;
    if (!fallbackMetal && !apiMetal) return null;

    const toStandards = (list) =>
      Array.isArray(list)
        ? list.map((x) => (typeof x === "string" ? x : x?.text || "")).filter(Boolean)
        : [];
    const toBullets = (list) =>
      Array.isArray(list)
        ? list.map((x) => (typeof x === "string" ? x : x?.text || "")).filter(Boolean)
        : [];
    const toPairRows = (list) =>
      Array.isArray(list)
        ? list
            .map((x) => ({
              label: x?.label || "",
              value: x?.value || "",
              unit: x?.unit || x?.units || "",
              reference: x?.reference || x?.standard || x?.astm || "",
            }))
            .filter((x) => x.label || x.value)
        : [];
    // Non-cross-section accessories use the same flexible row shape as drywall / ceiling metal
    // frames, so the public Dimensions table can build dynamic columns and apply the same
    // filters (stress columns hidden, empty rows dropped, zinc coating propagated, etc.).
    const toDimensions = (list) => toFlexibleDimensionsTableRows(list);

    // Per-label fixups for the Material Properties table. Each entry can:
    //   * rewrite the label (e.g. fix truncations like "Sound Reductio" → "Sound Reduction",
    //     "Galvanizatio" → "Galvanization"), and/or
    //   * set a default unit when the API doesn't return one.
    const MATERIAL_PROPERTY_LABEL_RULES = [
      {
        match: /sound\s*reduc?t(?:i(?:o(?:n)?)?)?/i,
        label: "Sound Reduction",
        defaultUnit: "dB",
      },
      {
        match: /galvani[sz]a?t(?:i(?:o(?:n)?)?)?/i,
        label: "Galvanization",
        defaultUnit: "--",
      },
    ];

    const normalizeMaterialPropertyRow = (row) => {
      if (!row || typeof row !== "object") return row;
      const labelText = String(row.label || "").trim();
      if (!labelText) return row;
      for (const rule of MATERIAL_PROPERTY_LABEL_RULES) {
        if (!rule.match.test(labelText)) continue;
        const nextLabel = rule.label || row.label;
        const explicitUnit = String(row.unit || row.units || "").trim();
        const nextUnit = explicitUnit || rule.defaultUnit || row.unit || "";
        return { ...row, label: nextLabel, unit: nextUnit };
      }
      return row;
    };

    const processMaterialProperties = (rows) =>
      expandMaterialPropertyRows(rows).map(normalizeMaterialPropertyRow);

    const stripStressKeys = (obj) =>
      Object.fromEntries(
        Object.entries(obj || {}).filter(([key]) => !isStressLabel(key))
      );

    const sectionDefaultsFromList = (list) =>
      stripStressKeys(sectionDefaultsFromLabelValuePairs(list));

    const defaultStaticImage = metalDetailById?.metal1?.heroImage || "";

    const accessoryPdfHints = [
      metalId,
      apiMetal?.accessoryKey,
      listMatch?.accessoryKey,
      fallbackMetal?.accessoryKey,
    ];

    const mergedAccessoryPdfs = (...sources) =>
      mapPublicProductPdfs(filterPdfRowsByProductSlug(pickPublicPdfArray(...sources), ...accessoryPdfHints));

    if (!fallbackMetal) {
      const source = apiMetal || listMatch;
      const isCrossSection = Boolean(source?.isCrossSection);
      const rawMat = toPairRows(source?.materialProperties);
      const adminDimensionColumns = Array.isArray(source?.dimensionsTableColumns)
        ? source.dimensionsTableColumns
        : [];
      const baseReturn = {
        id: metalId,
        heroTag: source?.heroTag || "ACCESSORIES",
        heroTitle: source?.heroTitle || source?.name || "Accessory",
        heroDescription: source?.heroDescription || "",
        heroDescriptionMore: source?.heroDescriptionMore || "",
        heroBullets: toBullets(source?.heroBullets),
        heroImage: resolveMediaUrl(source?.imageUrl) || defaultStaticImage,
        diagramUrl: source?.diagramUrl ?? "",
        standards: toStandards(source?.standards),
        materialProperties: processMaterialProperties(rawMat),
        adminDimensionColumns,
        pdfs: mergedAccessoryPdfs(apiMetal, listMatch),
      };
      if (isCrossSection) {
        const { sectionDimensionDefaults, dimensionsTable, dimensionColumns } = buildCrossSectionTableState(
          source?.sectionDimensions,
          source?.dimensionsTable,
          rawMat,
          adminDimensionColumns
        );
        return {
          ...baseReturn,
          isCrossSection: true,
          sectionDimensionDefaults,
          dimensionsTable,
          dimensionColumns,
          sectionDimensions: [],
        };
      }
      return {
        ...baseReturn,
        isCrossSection: false,
        sectionDimensionDefaults: sectionDefaultsFromList(source?.sectionDimensions),
        dimensionColumns: [],
        sectionDimensions: toPairRows(source?.sectionDimensions),
        dimensionsTable: toDimensions(source?.dimensionsTable),
      };
    }

    if (!apiMetal) {
      return {
        ...fallbackMetal,
        diagramUrl: fallbackMetal?.diagramUrl ?? "",
        isCrossSection: Boolean(fallbackMetal?.isCrossSection),
        sectionDimensionDefaults: fallbackMetal?.isCrossSection
          ? fallbackMetal.sectionDimensionDefaults || {}
          : sectionDefaultsFromList(fallbackMetal.sectionDimensions),
        dimensionColumns: [],
        adminDimensionColumns: [],
        materialProperties: processMaterialProperties(fallbackMetal.materialProperties || []),
        pdfs: mergedAccessoryPdfs(fallbackMetal, listMatch),
      };
    }

    const mergedIsCrossSection = Boolean(
      apiMetal?.isCrossSection ?? listMatch?.isCrossSection ?? fallbackMetal?.isCrossSection
    );

    const rawMaterialProps =
      toPairRows(apiMetal?.materialProperties).length > 0
        ? toPairRows(apiMetal?.materialProperties)
        : fallbackMetal.materialProperties || [];

    const mergedAdminColumns = Array.isArray(apiMetal?.dimensionsTableColumns)
      ? apiMetal.dimensionsTableColumns
      : Array.isArray(listMatch?.dimensionsTableColumns)
      ? listMatch.dimensionsTableColumns
      : [];

    if (mergedIsCrossSection) {
      const sectionSrc = apiMetal?.sectionDimensions ?? listMatch?.sectionDimensions;
      const dimSrc = apiMetal?.dimensionsTable ?? listMatch?.dimensionsTable;
      const { sectionDimensionDefaults, dimensionsTable, dimensionColumns } = buildCrossSectionTableState(
        sectionSrc,
        dimSrc,
        rawMaterialProps,
        mergedAdminColumns
      );
      return {
        ...fallbackMetal,
        isCrossSection: true,
        sectionDimensionDefaults,
        dimensionsTable,
        dimensionColumns,
        adminDimensionColumns: mergedAdminColumns,
        sectionDimensions: [],
        heroTag: apiMetal?.heroTag || fallbackMetal.heroTag,
        heroTitle: apiMetal?.heroTitle || apiMetal?.name || fallbackMetal.heroTitle,
        heroDescription: apiMetal?.heroDescription || fallbackMetal.heroDescription,
        heroDescriptionMore: apiMetal?.heroDescriptionMore || fallbackMetal.heroDescriptionMore,
        heroBullets: toBullets(apiMetal?.heroBullets).length
          ? toBullets(apiMetal?.heroBullets)
          : fallbackMetal.heroBullets,
        heroImage: resolveMediaUrl(apiMetal?.imageUrl) || fallbackMetal.heroImage,
        diagramUrl: apiMetal?.diagramUrl ?? listMatch?.diagramUrl ?? fallbackMetal?.diagramUrl ?? "",
        standards: toStandards(apiMetal?.standards).length ? toStandards(apiMetal?.standards) : fallbackMetal.standards,
        materialProperties: processMaterialProperties(rawMaterialProps),
        pdfs: mergedAccessoryPdfs(apiMetal, listMatch, fallbackMetal),
      };
    }

    const dimCore =
      toDimensions(apiMetal?.dimensionsTable).length > 0
        ? toDimensions(apiMetal?.dimensionsTable)
        : fallbackMetal.dimensionsTable || [];

    const mergedSectionDimensions =
      toPairRows(apiMetal?.sectionDimensions).length
        ? toPairRows(apiMetal?.sectionDimensions)
        : fallbackMetal.sectionDimensions;

    return {
      ...fallbackMetal,
      isCrossSection: false,
      sectionDimensionDefaults: sectionDefaultsFromList(mergedSectionDimensions),
      dimensionColumns: [],
      adminDimensionColumns: mergedAdminColumns,
      heroTag: apiMetal?.heroTag || fallbackMetal.heroTag,
      heroTitle: apiMetal?.heroTitle || apiMetal?.name || fallbackMetal.heroTitle,
      heroDescription: apiMetal?.heroDescription || fallbackMetal.heroDescription,
      heroDescriptionMore: apiMetal?.heroDescriptionMore || fallbackMetal.heroDescriptionMore,
      heroBullets: toBullets(apiMetal?.heroBullets).length ? toBullets(apiMetal?.heroBullets) : fallbackMetal.heroBullets,
      heroImage: resolveMediaUrl(apiMetal?.imageUrl) || fallbackMetal.heroImage,
      diagramUrl: apiMetal?.diagramUrl ?? listMatch?.diagramUrl ?? fallbackMetal?.diagramUrl ?? "",
      standards: toStandards(apiMetal?.standards).length ? toStandards(apiMetal?.standards) : fallbackMetal.standards,
      materialProperties: processMaterialProperties(rawMaterialProps),
      sectionDimensions: mergedSectionDimensions,
      dimensionsTable: dimCore,
      pdfs: mergedAccessoryPdfs(apiMetal, listMatch, fallbackMetal),
    };
  }, [apiMetal, apiSettled, fallbackMetal, listMatch, metalId]);

  const heroText = useMemo(() => {
    if (!metal) return "";
    return `${metal.heroDescription || ""}${metal.heroDescriptionMore || ""}`.trim();
  }, [metal]);

  // Identifiers used to classify the current accessory (e.g. detect Fiberglass insulation,
  // products that should hide the section dimensions panel, etc.). Built from every place the
  // product name/key can appear so backend variants are tolerated.
  const productIdentifierHaystacks = useMemo(
    () => [
      metal?.heroTitle,
      apiMetal?.heroTitle,
      apiMetal?.name,
      listMatch?.heroTitle,
      listMatch?.name,
      apiMetal?.accessoryKey,
      listMatch?.accessoryKey,
      metalId,
    ],
    [metal, apiMetal, listMatch, metalId]
  );

  // Plain "Fiberglass" insulation (NOT "Fiberglass Mesh Tape") uses a custom Dimensions
  // table layout split into two rows — one for Rolls, one for Blankets.
  const isFiberglassProduct = useMemo(() => {
    if (!metal || metal?.isCrossSection) return false;
    const isMeshTape = productIdentifierHaystacks.some((value) =>
      /fiberglass\s*mesh/i.test(String(value || ""))
    );
    if (isMeshTape) return false;
    return productIdentifierHaystacks.some((value) =>
      /\bfiberglass\b/i.test(String(value || ""))
    );
  }, [metal, productIdentifierHaystacks]);

  // Rockwool insulation is not a metal product, so the "Zinc coating" column is repurposed
  // as "Standards" (e.g. ASTM / EN references). Matches "Rockwool", "Rock wool" and
  // products like "IRQ - ROCKWOOL INSULATION".
  const isRockwoolInsulationProduct = useMemo(() => {
    if (!metal || metal?.isCrossSection) return false;
    return productIdentifierHaystacks.some((value) =>
      /\brock\s*wool\b/i.test(String(value || ""))
    );
  }, [metal, productIdentifierHaystacks]);

  // Dynamic columns for the non-cross-section accessory dimensions table — mirrors the same
  // pattern used by Drywall / Ceiling metal frames. Columns are discovered from `dimensionsTable`
  // rows + `sectionDimensionDefaults`, the Yield/Ultimate stress columns are hidden, a generic
  // "Height" column is dropped when H1 covers it, and columns are sorted into the matching
  // canonical order (default / H1+H2 / H1+H2+W1+W2 / slot / shoulder+web / flange).
  /** Header label for a cross-section column — saved admin label first, generated fallback. */
  const crossSectionLabelFor = (columnKey) => {
    const cols = Array.isArray(metal?.adminDimensionColumns) ? metal.adminDimensionColumns : [];
    const match = cols.find((c) => normalizeKey(c?.key) === normalizeKey(columnKey));
    return match?.label || toHeaderLabel(columnKey);
  };

  const accessoryDimensionColumns = useMemo(() => {
    if (!metal || metal.isCrossSection || isFiberglassProduct) return [];

    // Saved admin columns are authoritative: render exactly what the API returns
    // (same keys, labels and order as in the dashboard manager). Rockwool keeps its
    // "Standards" header for the zinc-coating column.
    const adminCols = columnsFromAdmin(metal?.adminDimensionColumns);
    if (adminCols) {
      return adminCols.map(({ key, label }) =>
        isRockwoolInsulationProduct && normalizeKey(key) === "zinccoating"
          ? { key, label: "Standards" }
          : { key, label }
      );
    }

    const DEFAULT_ORDER = [
      "productcode",
      "width",
      "height",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const H1_H2_ORDER = [
      "productcode",
      "h1",
      "width",
      "h2",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const H1_H2_W1_W2_ORDER = [
      "productcode",
      "h1",
      "w1",
      "h2",
      "w2",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const SLOT_ORDER = [
      "productcode",
      "width",
      "height",
      "slotwidth",
      "slotspacing",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const SHOULDER_WEB_H1_H2_ORDER = [
      "productcode",
      "width",
      "webwidth",
      "h1",
      "h2",
      "shoulder",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];
    const FLANGE_ORDER = [
      "productcode",
      "flange",
      "width",
      "thickness",
      "length",
      "unit",
      "zinccoating",
    ];

    const baseKeys = buildDimensionColumnKeys(
      metal?.dimensionsTable,
      metal?.sectionDimensionDefaults
    ).filter((key) => !HIDDEN_STRESS_KEYS.has(normalizeKey(key)));

    const normalizedSet = new Set(baseKeys.map((k) => normalizeKey(k)));
    const hasHeight = normalizedSet.has("height");
    const hasH1 = normalizedSet.has("h1");
    const hasH2 = normalizedSet.has("h2");
    const hasW1 = normalizedSet.has("w1");
    const hasW2 = normalizedSet.has("w2");
    const hasH1H2W1W2 = hasH1 && hasH2 && hasW1 && hasW2;
    const hasSlots =
      normalizedSet.has("slotwidth") && normalizedSet.has("slotspacing");
    const hasShoulderWeb =
      normalizedSet.has("shoulder") &&
      normalizedSet.has("webwidth") &&
      hasH1 &&
      hasH2;
    // Match "flange", "flanges", "flangeWidth", etc. — any column whose normalized name starts with "flange".
    const hasFlange = Array.from(normalizedSet).some((k) => k.startsWith("flange"));

    // Hide the generic "Height" column when both H1 and H2 are present (covered by H1/H2
    // columns) or when Flange is present (Height doesn't apply to flange profiles).
    const shouldHideHeight = (hasH1 && hasH2) || hasFlange;
    // Drywall Control Joint (also titled just "Control joint" in some places): H1 doesn't
    // apply to this product — drop it from the Dimensions table entirely.
    const isControlJointProduct = productIdentifierHaystacks.some((value) =>
      /\bcontrol\s*joint\b/i.test(String(value || ""))
    );
    // When only H1 exists alongside an explicit "Height" column they describe the same value
    // — keep "Height" and drop H1 to avoid duplicate columns. Also force-hide H1 for
    // products that explicitly opt out of an H1 column (e.g. Control Joint).
    const shouldHideH1 = (hasH1 && !hasH2 && hasHeight) || (hasH1 && isControlJointProduct);
    // When H1/H2 + W1/W2 are present, hide the generic "Width" column too — width info
    // comes from W1/W2.
    const shouldHideWidth = hasH1H2W1W2;
    const keys = baseKeys.filter((k) => {
      const n = normalizeKey(k);
      if (shouldHideHeight && n === "height") return false;
      if (shouldHideH1 && n === "h1") return false;
      if (shouldHideWidth && n === "width") return false;
      return true;
    });

    const activeOrder = hasH1H2W1W2
      ? H1_H2_W1_W2_ORDER
      : hasShoulderWeb
      ? SHOULDER_WEB_H1_H2_ORDER
      : hasH1 && hasH2
      ? H1_H2_ORDER
      : hasFlange
      ? FLANGE_ORDER
      : hasSlots
      ? SLOT_ORDER
      : DEFAULT_ORDER;
    const orderIndex = (normalized) => {
      const idx = activeOrder.indexOf(normalized);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    const sortedKeys = keys
      .map((key, index) => ({ key, index, normalized: normalizeKey(key) }))
      .sort((a, b) => {
        // When only H1 exists (no H2 and no explicit Height), treat H1 as the "height"
        // slot for ordering so it lands in the same column position as Height would.
        const treatH1AsHeight = hasH1 && !hasH2 && !hasHeight;
        const aOrderKey = treatH1AsHeight && a.normalized === "h1" ? "height" : a.normalized;
        const bOrderKey = treatH1AsHeight && b.normalized === "h1" ? "height" : b.normalized;
        const diff = orderIndex(aOrderKey) - orderIndex(bOrderKey);
        return diff !== 0 ? diff : a.index - b.index;
      })
      .map(({ key }) => key);

    return sortedKeys.map((key) => {
      const normalized = normalizeKey(key);
      // Rockwool isn't a metal product, so the "Zinc coating" column is repurposed
      // as "Standards" for that family (per-row standards references).
      if (isRockwoolInsulationProduct && normalized === "zinccoating") {
        return { key, label: "Standards" };
      }
      const label =
        hasH1 && !hasH2 && !hasHeight && normalized === "h1"
          ? "Height"
          : toHeaderLabel(key);
      return { key, label };
    });
  }, [metal, isFiberglassProduct, isRockwoolInsulationProduct, productIdentifierHaystacks]);

  const formatDimensionCell = (raw) => {
    if (raw == null) return "";
    if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
      return String(raw);
    }
    if (Array.isArray(raw)) {
      return raw.map((v) => formatDimensionCell(v)).filter(Boolean).join(", ");
    }
    if (typeof raw === "object") {
      const language = String(i18n.language || "en").toLowerCase();
      const known =
        raw[language] ??
        raw.value ??
        raw.text ??
        raw.label ??
        raw.amount ??
        raw.display ??
        raw.en ??
        raw.name ??
        null;
      if (typeof known === "string" || typeof known === "number") return String(known);
      for (const v of Object.values(raw)) {
        if (typeof v === "string" || typeof v === "number") return String(v);
      }
      return "";
    }
    return "";
  };

  const isStressRoleRow = (row) => {
    const role = String(row?.role || "").toLowerCase();
    if (!role) return false;
    return /yield\s*stress/.test(role) || /ultimate\s*stress/.test(role);
  };

  // Rows visible in the non-cross-section accessory table: drop stress role rows and rows
  // that are empty across every visible column (matches drywall / ceiling behavior).
  const accessoryVisibleDimensionRows = useMemo(() => {
    if (!metal || metal.isCrossSection || isFiberglassProduct) return [];
    const rows = Array.isArray(metal?.dimensionsTable) ? metal.dimensionsTable : [];
    const defaults = metal?.sectionDimensionDefaults || {};
    const hasDefaults = Object.keys(defaults).length > 0;
    const sourceRows = rows.length === 0 && hasDefaults ? [{}] : rows;
    return sourceRows
      .filter((row) => !isStressRoleRow(row))
      .filter((row) =>
        accessoryDimensionColumns.some(
          ({ key }) => formatDimensionCell(getDimensionValue(row, key, defaults)).trim() !== ""
        )
      );
  }, [metal, isFiberglassProduct, accessoryDimensionColumns]);

  // Zinc coating is normally a frame-wide spec — propagate the first non-empty value to every
  // row. For Rockwool the column is repurposed as per-row "Standards", so no propagation.
  const accessorySharedZincCoating = useMemo(() => {
    if (!metal || metal.isCrossSection || isFiberglassProduct) return "";
    if (isRockwoolInsulationProduct) return "";
    const rows = Array.isArray(metal?.dimensionsTable) ? metal.dimensionsTable : [];
    const defaults = metal?.sectionDimensionDefaults || {};
    for (const row of rows) {
      const value = formatDimensionCell(
        getDimensionValue(row, "zincCoating", defaults)
      ).trim();
      if (value) return value;
    }
    return "";
  }, [metal, isFiberglassProduct, isRockwoolInsulationProduct]);

  const getAccessoryCellValue = (row, key) => {
    const formatted = formatDimensionCell(
      getDimensionValue(row, key, metal?.sectionDimensionDefaults || {})
    );
    if (!formatted.trim() && normalizeKey(key) === "zinccoating" && accessorySharedZincCoating) {
      return accessorySharedZincCoating;
    }
    return formatted;
  };

  // Builds Fiberglass rows for the Dimensions table. Each cell can come from:
  //   1) the matching `dimensionsTable[index]` field (if the backend persists it), or
  //   2) a `sectionDimensions` entry whose label mentions both the column name AND the type
  //      (e.g. "Rolls Density", "Density - Blankets", "Density (Rolls)"), or
  //   3) a generic `sectionDimensions` entry whose label is just the column name (e.g. "Density").
  //      The generic value is further parsed for embedded type-prefixed segments like
  //      "Rolls: 16 / 24 / 36 / 48 | Blankets: 64" so each row gets only its own part.
  //      If no type prefix is found, the full value is shared between both rows.
  const fiberglassRows = useMemo(() => {
    if (!isFiberglassProduct) return [];

    const normalize = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[\s_\-()/]+/g, "")
        .trim();

    // Parses a value like "Rolls: 16 / 24 / 36 / 48 | Blankets: 64" into segments
    // keyed by the normalized type label. Returns {} if no "Type: value" pairs exist.
    const parseTypeSegments = (value) => {
      const text = String(value || "");
      const segments = text.split(/\s*[|;\n]\s*/);
      const result = {};
      for (const segment of segments) {
        const colonIdx = segment.indexOf(":");
        if (colonIdx === -1) continue;
        const key = normalize(segment.slice(0, colonIdx));
        const val = segment.slice(colonIdx + 1).trim();
        if (key && val) result[key] = val;
      }
      return result;
    };

    const sectionEntries = Array.isArray(metal?.sectionDimensions)
      ? metal.sectionDimensions
          .map((row) => {
            const value = String(row?.value || "").trim();
            const unit = String(row?.unit || row?.units || "").trim();
            return {
              label: String(row?.label || "").trim(),
              normalizedLabel: normalize(row?.label),
              value: value && unit ? `${value} ${unit}` : value || unit,
            };
          })
          .filter((entry) => entry.normalizedLabel && entry.value)
      : [];

    const findSectionValue = (typeLabel, columnAliases) => {
      const normalizedType = normalize(typeLabel);
      const normalizedAliases = columnAliases.map(normalize);

      // 1) Label includes both the column and the type → that entry's whole value belongs to this row.
      const typed = sectionEntries.find((entry) =>
        normalizedAliases.some(
          (alias) => entry.normalizedLabel.includes(alias) && entry.normalizedLabel.includes(normalizedType)
        )
      );
      if (typed) return typed.value;

      // 2) Label is just the column name (e.g. "Density"). The value might still contain
      //    type-prefixed segments like "Rolls: ... | Blankets: ..." — parse them out.
      const generic = sectionEntries.find((entry) =>
        normalizedAliases.some((alias) => entry.normalizedLabel === alias)
      );
      if (!generic) return "";

      const segments = parseTypeSegments(generic.value);
      if (Object.keys(segments).length > 0) {
        return segments[normalizedType] || "";
      }

      // No type prefixes — share the full value between rows.
      return generic.value;
    };

    return FIBERGLASS_TYPE_LABELS.map((typeLabel, index) => {
      const row = (metal?.dimensionsTable || [])[index] || {};
      const pick = (rowKeys, sectionAliases) => {
        for (const key of rowKeys) {
          const candidate = String(row?.[key] || "").trim();
          if (candidate) return candidate;
        }
        return findSectionValue(typeLabel, sectionAliases);
      };

      return {
        type: row?.type || row?.role || typeLabel,
        thickness: pick(["thickness"], ["thickness"]),
        width: pick(["width"], ["width"]),
        length: pick(["length"], ["length"]),
        density: pick(["density"], ["density", "densitykgm3", "densitykgperm3", "densitykgm"]),
        standards: pick(["standards", "standard"], ["standards", "standard", "reference"]),
      };
    });
  }, [isFiberglassProduct, metal]);

  const showDimensions = useMemo(() => {
    if (!metal) return false;
    const title = String(metal?.heroTitle || "").toLowerCase();
    const isAnchorOrScrew = title.includes("anchor bolt") || title.includes("screw");
    if (isAnchorOrScrew) return false;
    // Some accessories have no meaningful dimensions table — hide the whole section.
    // e.g. "IRQ - CROSS CONNECTOR FOR CD60" and similar cross-connector variants.
    const hidesDimensionsTable = productIdentifierHaystacks.some((value) =>
      /\bcross\s*connector(s)?\b/i.test(String(value || ""))
    );
    if (hidesDimensionsTable) return false;
    if (metal.isCrossSection) {
      const rows = metal.dimensionsTable || [];
      if (!rows.length) return false;
      const cols = metal.dimensionColumns || [];
      if (!cols.length) return false;
      const defaults = metal.sectionDimensionDefaults || {};
      return rows.some((row) =>
        cols.some((key) => String(getDimensionValue(row, key, defaults) ?? "").trim() !== "")
      );
    }
    if (isFiberglassProduct) return true;
    return accessoryDimensionColumns.length > 0 && accessoryVisibleDimensionRows.length > 0;
  }, [
    metal,
    isFiberglassProduct,
    accessoryDimensionColumns,
    accessoryVisibleDimensionRows,
    productIdentifierHaystacks,
  ]);

  const visibleTabs = useMemo(
    () => TAB_ITEMS.filter((tab) => (tab.id === "dimensions" ? showDimensions : true)),
    [showDimensions]
  );

  useEffect(() => {
    setActiveTab("standards");
  }, [metalId]);

  if (!metal && !resolved) {
    return null;
  }

  if (!metal) {
    return <Navigate to="/accessories" replace />;
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    const targetSection = sectionRefs.current[tabId];
    if (!targetSection) return;

    const topOffset = 100;
    const sectionTop = targetSection.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: sectionTop - topOffset,
      behavior: "smooth",
    });
  };

  const specDiagramSrc = metal.isCrossSection ? resolveMediaUrl(metal.diagramUrl || "") : "";

  // Products without meaningful section dimensions — hide the right-hand panel:
  //   * Corner Tape / Fiberglass Mesh Tape / Paper Joint Tape
  //   * Fire and Acoustic Sealant
  //   * All screws (Self Drilling, Self-Tapping, Wafer Head, etc.)
  //   * Anchor bolts (with/without sleeve), drop-in anchors, nylon wall plugs
  //   * Wire Hanger (e.g. "IRQ - WIRE HANGER")
  //   * Cross Connector (e.g. "IRQ - CROSS CONNECTOR FOR CD60")
  const hidesSectionDimensions = (() => {
    const patterns = [
      /(corner|fiberglass\s*mesh|paper\s*joint)[\s-]*tape/i,
      /fire\s*(?:and|&)?\s*acoustic\s*sealant/i,
      /\bscrew(s|ing)?\b/i,
      /\banchor\s*bolt(s)?\b/i,
      /\bdrop[\s-]*in\s*anchor(s)?\b/i,
      /\bnylon\s*wall\s*plug(s)?\b/i,
      /\bwire\s*hanger(s)?\b/i,
      /\bcross\s*connector(s)?\b/i,
    ];
    return productIdentifierHaystacks.some((value) => {
      const str = String(value || "");
      return patterns.some((re) => re.test(str));
    });
  })();

  const showSectionDimensionsPanel = !metal.isCrossSection && !hidesSectionDimensions;
  const specsGridUsesTwoColumns = showSectionDimensionsPanel || Boolean(specDiagramSrc);

  return (
    <div className="products-page metalDetailPage">
      <div className="products-page-content-guide">
        <Navbar theme="dark" />

        <section className="metalDetailHero" aria-label={metal.heroTitle}>
          <div className="metalDetailHero-inner">
            <div>
              <span className="metalDetailHero-tag">{metal.heroTag}</span>
              <h1 className="metalDetailHero-title">{metal.heroTitle}</h1>
              <p className="metalDetailHero-desc">{formatUnitSuperscriptText(heroText)}</p>
            </div>
            <div className="metalDetailHero-imageWrap">
              <img src={metal.heroImage} alt="" className="metalDetailHero-image" draggable={false} />
            </div>
          </div>
        </section>

        <div className="metalDetailTabsWrap">
          <nav className="metalDetailTabs" aria-label="Accessory detail sections">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`metalDetailTab ${activeTab === tab.id ? "is-active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </nav>
        </div>

        <section
          className="metalDetailSection"
          ref={(node) => {
            sectionRefs.current.standards = node;
          }}
        >
          <div className="metalDetailSection-inner">
            <h2 className="metalDetailHeading">{t("sections.products.detailTabs.standards")}</h2>
            <div className="metalStandardsGrid">
              {metal.standards.map((text, index) => (
                <div className="metalStandardsCard" key={`${index}-${text.slice(0, 12)}`}>
                  <span className="metalStandardsIndex">{String(index + 1).padStart(2, "0")}</span>
                  <p className="metalStandardsText">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="metalSpecsSection"
          ref={(node) => {
            sectionRefs.current.specifications = node;
          }}
        >
          <div className="metalDetailSection-inner">
            <h2 className="metalDetailHeading">{t("sections.products.detailTabs.specifications")}</h2>
            <div className={`metalSpecsGrid${specsGridUsesTwoColumns ? "" : " metalSpecsGrid--single"}`}>
              <div>
                <h3 className="metalSpecsBlockTitle">Material Properties</h3>
                <div className="metalSpecsRule" />
                <div className="metalSpecsTableWrap">
                  <table className="metalSpecsTable">
                    <thead>
                      <tr>
                        <th scope="col">Specifications</th>
                        <th scope="col">Units</th>
                        <th scope="col">Value</th>
                        <th scope="col">Standard</th>
                      </tr>
                    </thead>
                    <tbody>{renderMetalSpecificationTableBody(metal.materialProperties)}</tbody>
                  </table>
                </div>
              </div>

              {showSectionDimensionsPanel ? (
                <div>
                  <h3 className="metalSpecsBlockTitle">Section Dimensions</h3>
                  <div className="metalSpecsRule" />
                  <div className="metalSpecsTableWrap">
                    <table className="metalSpecsTable">
                      <thead>
                        <tr>
                          <th scope="col">Specifications</th>
                          <th scope="col">Units</th>
                          <th scope="col">Value</th>
                          <th scope="col">Standard</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderMetalSpecificationTableBody(
                          (metal.sectionDimensions || []).filter((row) => !isStressLabel(row?.label))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : specDiagramSrc ? (
                <div className="metalSpecsDiagramCol">
                  <h3 className="metalSpecsBlockTitle">Diagram</h3>
                  <div className="metalSpecsRule" />
                  <div className="metalSpecsDiagramWrap">
                    <img
                      src={specDiagramSrc}
                      alt={`${metal.heroTitle || "Product"} cross-section diagram`}
                      className="metalSpecsDiagramImg"
                      draggable={false}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {showDimensions ? (
          <section
            className="metalDimensionsSection"
            ref={(node) => {
              sectionRefs.current.dimensions = node;
            }}
          >
            <div className="metalDetailSection-inner">
              <h2 className="metalDetailHeading">{t("sections.products.detailTabs.dimensions")}</h2>

              <div className="metalDimensionsTableWrap">
                <table className="metalDimensionsTable">
                  <thead>
                    <tr>
                      {metal.isCrossSection ? (
                        (metal.dimensionColumns || []).map((columnKey) => (
                          <th key={`col-${columnKey}`}>{crossSectionLabelFor(columnKey)}</th>
                        ))
                      ) : isFiberglassProduct ? (
                        <>
                          <th>Type</th>
                          <th>Thickness</th>
                          <th>Width</th>
                          <th>Length</th>
                          <th>Density (kg/m³)</th>
                          <th>Unit</th>
                          <th>Standards</th>
                        </>
                      ) : (
                        accessoryDimensionColumns.map(({ key, label }) => (
                          <th key={`col-${key}`}>
                            {typeof label === "string" ? formatUnitSuperscriptText(label) : label}
                          </th>
                        ))
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {metal.isCrossSection ? (
                      (metal.dimensionsTable || []).map((row, index) => (
                        <tr key={`dim-row-${index}`}>
                          {(metal.dimensionColumns || []).map((columnKey) => (
                            <td key={`cell-${columnKey}-${index}`}>
                              {String(
                                getDimensionValue(
                                  row,
                                  columnKey,
                                  metal.sectionDimensionDefaults || {}
                                ) ?? ""
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : isFiberglassProduct ? (
                      fiberglassRows.map((row) => (
                        <tr key={`fiberglass-${row.type}`}>
                          <td>{row.type}</td>
                          <td>{formatUnitSuperscriptText(row.thickness)}</td>
                          <td>{formatUnitSuperscriptText(row.width)}</td>
                          <td>{formatUnitSuperscriptText(row.length)}</td>
                          <td>{formatUnitSuperscriptText(row.density)}</td>
                          <td>mm</td>
                          <td>{formatUnitSuperscriptText(row.standards)}</td>
                        </tr>
                      ))
                    ) : (
                      accessoryVisibleDimensionRows.map((row, index) => (
                        <tr key={`acc-dim-${row?.role || row?.id || index}-${index}`}>
                          {accessoryDimensionColumns.map(({ key }) => (
                            <td key={`acc-cell-${key}-${index}`}>
                              {formatUnitSuperscriptText(getAccessoryCellValue(row, key))}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ) : null}

        <SolutionDetailsDownloads
          items={(metal.pdfs || []).map((p) => ({
            id: p.id,
            title: p.title,
            href: p.href,
          }))}
          downloadsDescription="Technical PDFs and documents for this accessory."
        />

        <BoardsDetailExploreSlider />
        <AllSolutionsRequestNewProject />
      </div>
      <Footer />
    </div>
  );
};

export default MetalDetail;
